import React, { useRef, useEffect, useCallback, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import { AnimatePresence, motion } from "framer-motion";
import SidebarContent from "./SidebarContent";
import UIStore from "../../store/uiStore";
import SidebarDrawerHeader from "./SidebarDrawerHeader";
import SidebarDrawerBottomSection from "./SidebarDrawerBottomSection";
import SidebarCollapsedContent from "./SidebarCollapsedContent";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeProvider";

// Breakpoints for responsive design
const MOBILE_BREAKPOINT = 640;  // phones
const TABLET_BREAKPOINT = 940;  // tablets - match SidebarToggler breakpoint

const SIDEBAR_MIN_WIDTH = 256;
const SIDEBAR_MAX_WIDTH = 480;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const SIDEBAR_WIDTH_KEY = 'sidebarExpandedWidth';

const loadSidebarWidth = () => {
  try {
    const v = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (v) {
      const n = parseInt(v, 10);
      if (n >= SIDEBAR_MIN_WIDTH && n <= SIDEBAR_MAX_WIDTH) return n;
    }
  } catch { /* ignore */ }
  return SIDEBAR_MIN_WIDTH;
};

const SidebarDrawer = ({ children }) => {
  const sidebarRef = useRef(null);
  const showSidebar = UIStore((state) => state.showSidebar);
  const setShowSidebar = UIStore((state) => state.setShowSidebar);
  const suppressSidebarAnimation = UIStore((state) => state.suppressSidebarAnimation);
  const [screenSize, setScreenSize] = React.useState(() => {
    const width = window.innerWidth;
    if (width < MOBILE_BREAKPOINT) return 'mobile';
    if (width < TABLET_BREAKPOINT) return 'tablet';
    return 'desktop';
  });
  const navigate = useNavigate();
  const { theme } = useTheme();

  // User-resizable expanded width — desktop only, persisted in localStorage.
  const [expandedWidth, setExpandedWidth] = useState(loadSidebarWidth);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';
  const isDesktop = screenSize === 'desktop';

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < MOBILE_BREAKPOINT) setScreenSize('mobile');
      else if (width < TABLET_BREAKPOINT) setScreenSize('tablet');
      else setScreenSize('desktop');
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on mobile/tablet when clicking outside
  useClickOutside(sidebarRef, () => setShowSidebar(false), showSidebar && !isDesktop);

  const handleSparkClick = () => {
    navigate("/spark");
    // Close sidebar on mobile/tablet after navigation
    if (window.innerWidth < 940) {
      setShowSidebar(false);
    }
  };

  // ── Resize drag ────────────────────────────────────────────────────────────
  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = expandedWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [expandedWidth]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = e.clientX - dragStartX.current;
      const next = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, dragStartWidth.current + delta));
      setExpandedWidth(next);
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist final width (read via functional updater to get the latest
      // value, not the stale pre-drag dragStartWidth.current)
      setExpandedWidth(prev => {
        try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(prev)); } catch { /* ignore */ }
        return prev;
      });
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Calculate sidebar width based on screen size and state
  const getSidebarWidth = () => {
    if (isMobile || isTablet) {
      return showSidebar ? 280 : 0;
    }
    return showSidebar ? expandedWidth : SIDEBAR_COLLAPSED_WIDTH;
  };

  // Calculate main content margin
  const getMainMargin = () => {
    if (isMobile || isTablet) {
      return 0;
    }
    return showSidebar ? expandedWidth : SIDEBAR_COLLAPSED_WIDTH;
  };

  const sidebarWidth = getSidebarWidth();

  // Spring by default; instant when a mode-switch requested suppression so the
  // collapse doesn't compete with the heavy Nebula subtree mount.
  const sidebarSpring = { type: 'spring', stiffness: 300, damping: 30 };
  const sidebarTransition = suppressSidebarAnimation
    ? { duration: 0 }
    : { ...sidebarSpring, opacity: { duration: 0.2 } };
  const mainTransition = suppressSidebarAnimation ? { duration: 0 } : sidebarSpring;

  return (
    <>
      {/* Sidebar - hidden on mobile/tablet when closed, overlay when open */}
      <AnimatePresence initial={false}>
        {(isDesktop || showSidebar) && (
          <motion.div
            ref={sidebarRef}
            key="sidebar"
            className={`fixed top-0 left-0 h-full flex flex-col justify-between z-[100] border-r rounded-r-xl overflow-hidden bg-surface-sidebar
              ${theme === "dark" ? "border-edge-hex-232a34" : "border-edge-gray-200"}
              ${isDesktop && !showSidebar ? 'cursor-pointer' : ''}`}
            initial={{ 
              width: sidebarWidth, 
              opacity: 0, 
              x: (isMobile || isTablet) ? -280 : 0,
              boxShadow: '0 0 0 0 rgba(0,0,0,0)' 
            }}
            animate={{ 
              width: sidebarWidth, 
              opacity: 1, 
              x: 0,
              boxShadow: (showSidebar || isDesktop) ? '0 2px 16px 0 rgba(0,0,0,0.08)' : '0 0 0 0 rgba(0,0,0,0)' 
            }}
            exit={{ 
              width: (isMobile || isTablet) ? 280 : SIDEBAR_COLLAPSED_WIDTH, 
              opacity: 0, 
              x: (isMobile || isTablet) ? -280 : 0,
              boxShadow: '0 0 0 0 rgba(0,0,0,0)' 
            }}
            transition={sidebarTransition}
            style={{ minWidth: 0, maxWidth: (isMobile || isTablet) ? 280 : SIDEBAR_MAX_WIDTH }}
            onClick={(e) => {
              // Only expand on direct clicks to the container (empty space), not on buttons
              if (isDesktop && !showSidebar && e.target === e.currentTarget) {
                setShowSidebar(true);
              }
            }}
          >
            {/* Desktop collapsed state */}
            {isDesktop && !showSidebar && (
              <SidebarCollapsedContent onExpand={() => setShowSidebar(true)} handleSparkClick={handleSparkClick} />
            )}
            
            {/* Expanded state (desktop) or open state (mobile/tablet) */}
            {(showSidebar || (isDesktop && showSidebar)) && (
              <>
                <SidebarDrawerHeader onNotebookClick={() => setShowSidebar(false)} />
                <div className="flex-1 min-h-0">
                  <SidebarContent closeSidebar={() => setShowSidebar(false)} />
                </div>
                <SidebarDrawerBottomSection />

                {/* Resize handle — desktop expanded only */}
                {isDesktop && (
                  <div
                    onMouseDown={handleResizeMouseDown}
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize z-[101] group"
                    title="Drag to resize"
                  >
                    {/* Thin visible indicator on hover */}
                    <div className="h-full w-full group-hover:bg-fill-primary/30 dark:group-hover:bg-fill-accent/30 transition-colors duration-150" />
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay for mobile/tablet when sidebar is open */}
      <AnimatePresence>
        {showSidebar && !isDesktop && (
          <motion.div
            className="fixed inset-0 z-[99] bg-surface-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowSidebar(false)}
          />
        )}
      </AnimatePresence>

      {/* Main content (children) */}
      <motion.div
        className=""
        layout
        animate={{ marginLeft: getMainMargin() }}
        transition={mainTransition}
        style={{ minHeight: '100vh' }}
      >
        {children}
      </motion.div>
    </>
  );
};

export default SidebarDrawer;
